import { IDeleteWordingData } from 'modules/CustomersAndRestaurants/Restaurants/EditRestaurant/components/Forms/components/LocalizationForm/localizationState/localizationContext';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { localizationReducer } from './localizationReducer';
import {
	LocalizationContext,
	localizationInitialState,
	TLocalizationSelectedItem,
	TLocalizationSettingsMode,
	TTypeOfModalWindow,
} from './localizationContext';
import { LocalizationActionsTypes } from './localizationActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { EditCustomerContext, IEditCustomerContext } from '../../../../../editCustomerState/editCustomerContext';
import { useFetchData } from 'hooks/useFetchData';
import { ICustomerLocalizationGroup, IGetCustomerLocalizationInfo, IUpdateCustomerLocalization } from 'types';
import { getCustomerLocalizationInfo } from 'backend/customers';
import { deleteWording } from 'backend/adminSettings';

const LocalizationState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(localizationReducer, localizationInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { visibleForm } = useContext<IEditCustomerContext>(EditCustomerContext);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const [needRequest, setNeedRequest] = useState<boolean>(false);

	const params = new URL(document.location.href).searchParams;
	const customerID = params.get('customerID');

	const needLocalizationDataRequest =
		(customerID && visibleForm === 'localizationForm' && isFirstRender) || state.needRefetchWordings;

	const { response, refetch, loading } = useFetchData<IGetCustomerLocalizationInfo, string>(
		needRequest ? getCustomerLocalizationInfo : null,
		customerID || '',
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		setNeedRequest(!!needLocalizationDataRequest);
	}, [needLocalizationDataRequest]);

	useEffect(() => {
		setNeedRequest(!!state.needRefetchWordings);
	}, [state.needRefetchWordings]);

	useEffect(() => {
		if (state.needRefetchWordings) {
			refetch();
			dispatch({
				type: LocalizationActionsTypes.REFETCH_WORDINGS,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchWordings]);

	useEffect(() => {
		refetch();
	}, [customerID, visibleForm]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: LocalizationActionsTypes.SET_ALL_DATA,
				payload: {
					groups: response.data.groups,
				},
			});
			setIsFirstRender(false);
		}
	}, [response]);

	const refetchWordings = (value: boolean) => {
		dispatch({
			type: LocalizationActionsTypes.REFETCH_WORDINGS,
			payload: {
				needRefetch: value,
			},
		});
	};

	const setGroup = (value: TLocalizationSelectedItem | null) => {
		dispatch({
			type: LocalizationActionsTypes.SET_GROUP,
			payload: {
				group: value,
			},
		});
	};

	const setLanguage = (value: TLocalizationSelectedItem | null) => {
		dispatch({
			type: LocalizationActionsTypes.SET_LANGUAGE,
			payload: {
				language: value,
			},
		});
	};

	const removeWording = async (groupId: string, wordingId: string) => {
		try {
			showLoader();
			await deleteWording(wordingId, 'Customer');

			const updatedGroups: ICustomerLocalizationGroup[] = state.groups.map((group) =>
				group.id === groupId ? { ...group, items: group.items.filter((item) => item.id !== wordingId) } : group,
			);

			dispatch({
				type: LocalizationActionsTypes.REMOVE_WORDING,
				payload: {
					updatedGroups,
				},
			});
			dispatch({
				type: LocalizationActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: LocalizationActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const addInvalidField = (value: string) => {
		dispatch({
			type: LocalizationActionsTypes.ADD_INVALID_FIELD,
			payload: {
				invalidField: value,
			},
		});
	};

	const clearInvalidFields = () => {
		dispatch({
			type: LocalizationActionsTypes.CLEAR_INVALID_FIELDS,
		});
	};

	const setDataForSubmit = (value: IUpdateCustomerLocalization | null) => {
		dispatch({
			type: LocalizationActionsTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	};

	const setDeleteWordingData = (value: IDeleteWordingData | null) => {
		dispatch({
			type: LocalizationActionsTypes.SET_DELETE_WORDING_DATA,
			payload: {
				deleteWordingData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: LocalizationActionsTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeMode = (value: TLocalizationSettingsMode) => {
		dispatch({
			type: LocalizationActionsTypes.CHANGE_MODE,
			payload: {
				mode: value,
			},
		});
	};

	return (
		<LocalizationContext.Provider
			value={{
				refetchWordings,
				setGroup,
				setLanguage,
				removeWording,
				addInvalidField,
				clearInvalidFields,
				setDataForSubmit,
				setDeleteWordingData,
				changeTypeOfModalWindow,
				changeMode,
				needRefetchWordings: state.needRefetchWordings,
				loading,
				selectedGroup: state.selectedGroup,
				selectedLanguage: state.selectedLanguage,
				groups: state.groups,
				invalidFields: state.invalidFields,
				dataForSubmit: state.dataForSubmit,
				deleteWordingData: state.deleteWordingData,
				typeOfModalWindow: state.typeOfModalWindow,
				mode: state.mode,
			}}>
			{children}
		</LocalizationContext.Provider>
	);
};

export default LocalizationState;
